import { useEffect, useRef, type RefObject } from 'react';

// Webpack-5 / esbuild ESM-CJS interop, take 2.
//
//   `react-audio-player` ships as a self-contained webpack-1 UMD bundle
//   whose entry exports look like
//     module.exports = { default: ReactAudioPlayer, __esModule: true }
//   tsup emits `import * as Ns from 'react-audio-player'` + `Ns.default`
//   verbatim. CRA's webpack-5 then wraps that again at the host layer
//   (`so = n(34344); ao = n.t(so, 2)`) so the runtime sees `Ns` and
//   `Ns.default` collapsed onto the same wrapper object — the actual
//   class is one *more* `.default` away. A static `Ns.default ?? Ns`
//   gets folded by webpack into `so ?? ao` and still resolves to the
//   wrapper, crashing with React error #130 ("element type is invalid:
//   ... but got: object").
//
//   Walking through `.default` inside an opaque function call is the
//   only form webpack can't constant-fold — it has to evaluate the
//   chain at runtime, which is exactly what we need.
import * as ReactAudioPlayerNs from 'react-audio-player';
import type ReactAudioPlayerType from 'react-audio-player';

function unwrapEsmDefault<T>(mod: unknown): T {
  let value: unknown = mod;
  for (let i = 0; i < 4 && value != null; i += 1) {
    if (typeof value === 'function') return value as T;
    if (typeof value === 'object') {
      const next = (value as { default?: unknown }).default;
      if (next !== undefined && next !== value) {
        value = next;
        continue;
      }
    }
    break;
  }
  return value as T;
}

const ReactAudioPlayer = unwrapEsmDefault<typeof ReactAudioPlayerType>(ReactAudioPlayerNs);
type ReactAudioPlayer = ReactAudioPlayerType;

import { AVATAR_EMBED_EVENT } from '../../host/embedProtocol';
import {
  isAutoplayBlockedError,
  notifyAudioAccessRequired,
  playerEnded,
  playerReady,
  type AvatarRef as AudioAvatarRef
} from '../utils/audioUtils';
import {
  registerAudioSpeechPlayback,
  type SpeechPlaybackHandle
} from '../utils/speechPlayback';

interface AvatarAudioLayerProps {
  audioPlayer: RefObject<ReactAudioPlayer | null>;
  audioSource: string | null;
  avatarRef: RefObject<AudioAvatarRef | null>;
  audioElementId?: string;
  onSpeakingEnd?: () => void;
  onSpeakingStart?: () => void;
  setAudioSource: (source: string | null) => void;
  setPlaying: (value: boolean) => void;
  setRecord: (value: boolean) => void;
  setSpeak: (value: boolean) => void;
}

function postEmbedSpeakingEvent(type: string): void {
  if (window.parent !== window) {
    window.parent.postMessage({ type }, '*');
  }
}

function dispatchAudioSyncEvent(currentTime: number, duration: number, isPlaying: boolean): void {
  window.dispatchEvent(new CustomEvent('avatar:audioTimeSync', {
    detail: { currentTime, duration, isPlaying }
  }));
}

export function AvatarAudioLayer({
  audioPlayer,
  audioSource,
  avatarRef,
  audioElementId,
  onSpeakingEnd,
  onSpeakingStart,
  setAudioSource,
  setPlaying,
  setRecord,
  setSpeak
}: AvatarAudioLayerProps) {
  // Pause/stop control (Aug 29 2026): each utterance this element plays is
  // registered with the shared speech playback registry, so the call
  // surface's pause/stop buttons control it. The element is REUSED across
  // utterances (src swaps), so the registration lifecycle is: register on
  // onPlay, retire when the utterance ends or the src is replaced/cleared.
  const playbackHandleRef = useRef<SpeechPlaybackHandle | null>(null);

  const settleEnded = () => {
    playerEnded(setAudioSource, setSpeak, setPlaying, setRecord, avatarRef as RefObject<AudioAvatarRef>);
    postEmbedSpeakingEvent(AVATAR_EMBED_EVENT.speakingEnded);
    onSpeakingEnd?.();
  };

  // Src replaced or cleared (barge-in, new turn): the previous utterance's
  // registration must not keep the control visible.
  useEffect(() => {
    return () => {
      playbackHandleRef.current?.markEnded();
      playbackHandleRef.current = null;
    };
  }, [audioSource]);

  return (
    <>
      <audio
        id={audioElementId || 'remoteAudio'}
        autoPlay
        playsInline
        webkit-playsinline="true"
        controls={false}
        style={{
          position: 'fixed',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
          left: 0,
          bottom: 0
        }}
        preload="auto"
        x-webkit-airplay="deny"
      >
        <track kind="captions" />
        Your browser does not support the audio element.
      </audio>

      <ReactAudioPlayer
        src={audioSource ?? ''}
        ref={audioPlayer}
        onEnded={() => {
          playbackHandleRef.current?.markEnded();
          playbackHandleRef.current = null;
          settleEnded();
        }}
        onError={(event: unknown) => {
          if (!audioSource) {
            return;
          }

          // The MediaError shape is `audioEl.error` not the synthetic event;
          // pull it out so we can detect autoplay-blocked errors and surface
          // the permission chip rather than silently freezing the visemes.
          // See Slice 8 of the avatar fixes plan, May 11 2026.
          const audioEl = audioPlayer.current?.audioEl?.current;
          const mediaError = audioEl?.error;
          console.error('Audio playback error:', event, { mediaError });
          if (avatarRef.current?.stopVisemeAnimation) {
            avatarRef.current.stopVisemeAnimation();
          }
          if (isAutoplayBlockedError(mediaError) || isAutoplayBlockedError(event)) {
            notifyAudioAccessRequired('AvatarAudioLayer onError');
          }
        }}
        onCanPlayThrough={() => playerReady(audioPlayer as RefObject<ReactAudioPlayer>, setPlaying)}
        onPlay={() => {
          const audioEl = audioPlayer.current?.audioEl?.current;
          if (audioEl) {
            dispatchAudioSyncEvent(audioEl.currentTime, audioEl.duration, true);

            // Register the utterance for the pause/stop control. onPlay
            // also fires on RESUME after a pause — don't re-register (the
            // existing registration is still active and mid-pause).
            //
            // `audioSource &&`: onPlay ALSO fires when useAudioPlayback's
            // unlock ritual plays a muted silent WAV through this element
            // inside a user gesture (it swaps src directly on the element;
            // the audioSource prop stays null). That play is not speech.
            // Registering it leaked a phantom "speaking" playback that never
            // ended — the pause/stop control then rendered for the whole of
            // every CamlinVoice-driven live call (whose real voice is the
            // unpausable remoteAudio RTP stream) with buttons wired to a
            // dead, empty element. Keith hit exactly this on production
            // Decision Memory, Aug 29 2026.
            if (audioSource && !playbackHandleRef.current?.isActive()) {
              playbackHandleRef.current = registerAudioSpeechPlayback({
                kind: 'runtime-audio',
                audio: audioEl,
                // The element already publishes its clock at 16ms via
                // onListen below; the helper only adds the paused-position
                // heartbeat.
                dispatchSyncWhilePlaying: false,
                onStop: settleEnded
              });
            }
          }
          postEmbedSpeakingEvent(AVATAR_EMBED_EVENT.speakingStarted);
          onSpeakingStart?.();
        }}
        onPause={() => {
          const audioEl = audioPlayer.current?.audioEl?.current;
          if (audioEl) {
            dispatchAudioSyncEvent(audioEl.currentTime, audioEl.duration, false);
          }
          postEmbedSpeakingEvent(AVATAR_EMBED_EVENT.speakingEnded);
          onSpeakingEnd?.();
        }}
        listenInterval={16}
        onListen={(currentTime: number) => {
          const audioEl = audioPlayer.current?.audioEl?.current;
          if (audioEl && !audioEl.paused) {
            dispatchAudioSyncEvent(currentTime, audioEl.duration, true);
          }
        }}
        preload={audioSource ? 'auto' : 'none'}
        crossOrigin="anonymous"
      />
    </>
  );
}

export default AvatarAudioLayer;
